import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaGlobeAfrica, FaEdit, FaUserPlus, FaUserTie, FaGlobe, FaMoneyCheckAlt, FaUsers,
  FaSitemap, FaClipboardCheck, FaFileSignature, FaChartBar, FaTasks, FaComments,
} from 'react-icons/fa';
import { AuthContext } from '../authProvider.mjs';
import DelegationModal from '../auditor/components/DelegationModal.mjs';
import DirectorDelegationModal from '../auditor/components/DirectorDelegationModal.mjs';
import { HomeCard } from './HomeCard.mjs';

/**
 * Each staff role's dashboard, and the cards on it, in one place.
 *
 * The home page shows three cards: the claimant's own dashboard, filing a
 * claim, and ONE card for the person's role. That card opens the role's
 * dashboard - a page of cards (RoleDashboardPage): the role's approval work
 * first, then anything delegated to the person, then the role's other tools.
 * The approval pages themselves (the auditor's three tabs, the directors'
 * report lists) keep their own addresses, which emails and the monthly
 * report's Back buttons link to.
 *
 * These used to be separate cards on the home page, which grew to six for an
 * auditor and put the same tool in a different place for each role.
 *
 * Showing a card is convenience only. Every route and endpoint behind these
 * cards checks the role (and region) again.
 */

// Each role's dashboard. `regional` roles work within one region, which the
// dashboard's band names; the others see every region.
export const ROLE_DASHBOARDS = {
  admin: {
    path: '/admin/links', title: 'Admin Dashboard', label: 'Admin', icon: FaGlobeAfrica, regional: true,
    blurb: 'Claims approval, monthly summary reports, reports and user management',
  },
  auditor: {
    path: '/auditor', title: 'Auditor Dashboard', label: 'Auditor', icon: FaEdit, regional: false,
    blurb: 'Reports approval, delegation, PTAE and reports',
  },
  regional_director: {
    path: '/regional-director', title: 'Regional Director Dashboard', label: 'Regional Director',
    icon: FaUserTie, regional: true,
    blurb: 'Reports approval, reports and user management',
  },
  global_director: {
    path: '/global-director', title: 'Global Director Dashboard', label: 'Global Director',
    icon: FaGlobe, regional: false,
    blurb: 'Final reports approval, reports and user management',
  },
  finance_director: {
    path: '/finance-director', title: 'Finance Director Dashboard', label: 'Finance Director',
    icon: FaMoneyCheckAlt, regional: false,
    blurb: 'Final reports approval, PTAE approvals, reports and user management',
  },
};

// The Reports module admits all five staff roles; the server decides which
// regions each one sees.
const REPORTS = {
  key: 'reports', icon: FaChartBar, title: 'Reports', path: '/reports',
  blurb: 'Finally approved spend, PTAE funding and claim downloads',
};

/**
 * Clarification questions - review work, so right after the approvals and any
 * delegated work. Admins answer them; the reviewers ask them instead of
 * sending a claim or report back. The bell in the navbar carries the counts.
 */
const QUESTIONS = {
  admin: {
    key: 'questions', icon: FaComments, title: 'Questions', path: '/clarifications',
    blurb: 'Questions from the reviewers about your region\'s claims and reports',
  },
  reviewer: {
    key: 'questions', icon: FaComments, title: 'Questions', path: '/clarifications',
    blurb: 'Ask about a claim or report instead of sending it back, and read the answers',
  },
};

// The same four roles the /user-management route admits.
const USER_MANAGEMENT = {
  key: 'user-management', icon: FaUsers, title: 'User Management', path: '/user-management',
  blurb: 'Manage user roles and permissions',
};

/**
 * The role's own approval work - always the first card on its dashboard.
 *
 * Directors and the finance director share the report lists their roles
 * already had; the finance director's is the global director's board, which
 * titles itself by role.
 */
const APPROVALS = {
  admin: {
    key: 'claims-approval', icon: FaGlobeAfrica, title: 'Claims Approval', path: '/admin/dashboard',
    blurb: 'Claims in your region',
  },
  auditor: {
    key: 'reports-approval', icon: FaTasks, title: 'Reports Approval', path: '/auditor-dashboard',
    blurb: 'Monthly summary reports, claims pre-approval and contractor approvals',
  },
  regional_director: {
    key: 'reports-approval', icon: FaTasks, title: 'Reports Approval', path: '/regional-director-dashboard',
    blurb: 'Review and approve your region\'s monthly reports',
  },
  global_director: {
    key: 'reports-approval', icon: FaTasks, title: 'Reports Approval', path: '/global-director-dashboard',
    blurb: 'Final approval for all regional reports',
  },
  finance_director: {
    key: 'reports-approval', icon: FaTasks, title: 'Reports Approval', path: '/global-director-dashboard',
    blurb: 'Final approval for all regional reports',
  },
};

// Everything else each role can do, after its approvals and delegated work.
const ROLE_TOOLS = {
  admin: [
    { key: 'summary-reports', icon: FaEdit, title: 'Monthly Summary Report', path: '/dashboard/monthly-reports',
      blurb: 'Generate and submit your region\'s monthly summary reports' },
    // With the other admin tools rather than as a button on the claims
    // dashboard, where it sat among the rate-status controls and was missed.
    { key: 'file-for-someone', icon: FaUserPlus, title: 'File a Claim for Someone', path: '/admin/file-claim',
      blurb: 'Enter and submit a claim for someone in your region' },
    REPORTS,
    USER_MANAGEMENT,
  ],
  auditor: [
    // Only an auditor can create a delegation - POST /auditor/delegations is
    // auditor-only - so a delegate covering for one never gets this card.
    { key: 'delegate-review', icon: FaUserTie, title: 'Delegate Review', action: 'delegate',
      blurb: 'Hand monthly report review to someone else, by region, while you are away' },
    { key: 'ptae-management', icon: FaSitemap, title: 'PTAE Management', path: '/ptae-management',
      blurb: 'Chart of accounts for each region' },
    REPORTS,
  ],
  // The director decides who covers their approval while they are away (the
  // user, 2026-10-22) - the admin used to, report by report, at submission.
  regional_director: [
    { key: 'delegate-approval', icon: FaUserTie, title: 'Delegate Approval', action: 'delegate-approval',
      blurb: 'Let someone approve your region\'s monthly reports while you are away' },
    REPORTS,
    USER_MANAGEMENT,
  ],
  global_director: [
    { key: 'delegate-approval', icon: FaUserTie, title: 'Delegate Approval', action: 'delegate-approval',
      blurb: 'Let someone give final approval of monthly reports while you are away' },
    REPORTS,
    USER_MANAGEMENT,
  ],
  finance_director: [
    // The finance director is the final approver for every chart-of-accounts
    // addition, edit, suspension and import.
    { key: 'ptae-approvals', icon: FaClipboardCheck, title: 'PTAE Management', path: '/ptae-approvals',
      blurb: 'Approve additions, edits and suspensions · view all regions' },
    REPORTS,
    USER_MANAGEMENT,
  ],
};

// From the server-verified flags, never user.role (see RoleRequired).
const roleOf = (auth) => {
  if (auth.isAdmin) return 'admin';
  if (auth.isAuditor) return 'auditor';
  if (auth.isRegionalDirector) return 'regional_director';
  if (auth.isGlobalDirector) return 'global_director';
  if (auth.isFinanceDirector) return 'finance_director';
  return null;
};

/**
 * Work delegated to this person, as cards for their own dashboard.
 *
 * Both are server-decided (`capabilities` on /auth/status) and temporary: a
 * card goes when the cover lapses, at the same moment the authority does.
 */
/** "Covering regional approval for Jane until <local time> (East Africa)". */
const directorCoverNote = (directorCover) => (directorCover || [])
  .map(c => `Covering ${c.stage === 'final' ? 'final' : 'regional'} approval for ${c.coveringForName}`
    + (c.until ? ` until ${new Date(c.until).toLocaleString()}` : '')
    + (c.regions?.length ? ` (${c.regions.join(', ')})` : ''))
  .join(' · ') || null;

const delegatedCards = ({ isAuditor, isAuditorDelegate, auditorCover, reportApproval, directorCover }) => {
  const cards = [];

  // Standing in for an auditor. The role flag alone never showed this, so a
  // stand-in who had accepted the handover had no way to the board. A real
  // auditor already has it as their Reports Approval card.
  if (isAuditorDelegate && !isAuditor) {
    // "Covering for X until <their own local time>" - the teams are in
    // different timezones, so the end of cover is formatted by the browser.
    const note = (auditorCover?.covering || [])
      .map(c => `Covering for ${c.coveringForName}`
        + (c.until ? ` until ${new Date(c.until).toLocaleString()}` : '')
        + (c.regions?.length ? ` (${c.regions.join(', ')})` : ''))
      .join(' · ') || null;
    cards.push({
      key: 'auditor-cover', icon: FaEdit, title: 'Auditor Review', path: '/auditor-dashboard',
      blurb: 'Monthly reports for the regions you are covering', note,
    });
  }

  // Named to approve a monthly report, or covering a director's approval by
  // their delegation. Null for a finance director, who reaches the same
  // reports from their own board.
  const named = reportApproval?.reports || [];
  // The one to open: whichever is actually waiting on them, else the newest.
  const first = named.find(r => r.awaitingMe) || named[0];
  const coverNote = directorCoverNote(directorCover);
  if (first) {
    const waiting = reportApproval.awaitingCount || 0;
    const onlyByCover = named.every(r => r.viaDelegation);
    cards.push({
      key: 'named-reports', icon: FaFileSignature,
      title: waiting > 0 ? 'Reports awaiting you' : 'Reports you approve',
      path: `/monthly-report/${first.reportId}`,
      blurb: waiting > 0
        ? `${waiting} waiting on your approval`
        : onlyByCover
          ? `${named.length} report${named.length === 1 ? '' : 's'} in the regions you cover, none waiting on you yet`
          : `Named on ${named.length} report${named.length === 1 ? '' : 's'}, none waiting on you yet`,
      note: coverNote || (reportApproval.until
        ? `Your cover ends ${new Date(reportApproval.until).toLocaleString()}`
        : null),
    });
  }
  // Covering with nothing at that stage yet shows no card - nothing to open.
  // The home page's role card says "Covering approval for ..." meanwhile
  // (delegationNote), so someone who has just accepted can see it took.

  return cards;
};

/**
 * The signed-in person's role dashboard, the cards it holds, and a one-line
 * summary of delegated work for the home page's role card - so someone who
 * has just accepted cover can see where it went.
 */
export const useRoleTools = () => {
  const auth = useContext(AuthContext);
  const role = roleOf(auth);

  const covering = auth.isAuditorDelegate && !auth.isAuditor
    ? (auth.auditorCover?.covering || []).map(c => c.coveringForName)
    : [];
  const waiting = auth.reportApproval?.awaitingCount || 0;
  const directorCovering = [...new Set((auth.directorCover || []).map(c => c.coveringForName))];
  const delegationNote = [
    covering.length ? `Covering auditor review for ${covering.join(', ')}` : null,
    directorCovering.length ? `Covering approval for ${directorCovering.join(', ')}` : null,
    waiting > 0 ? `${waiting} report${waiting === 1 ? '' : 's'} awaiting your approval` : null,
  ].filter(Boolean).join(' · ') || null;

  return {
    dashboard: role ? ROLE_DASHBOARDS[role] : null,
    cards: role
      ? [
        APPROVALS[role],
        ...delegatedCards(auth),
        role === 'admin' && !auth.isAuditorDelegate ? QUESTIONS.admin : QUESTIONS.reviewer,
        ...ROLE_TOOLS[role],
      ]
      : [],
    delegationNote,
  };
};

/** The cards on the signed-in person's role dashboard. */
const RoleToolCards = () => {
  const navigate = useNavigate();
  const { cards } = useRoleTools();
  const [showDelegation, setShowDelegation] = useState(false);
  const [showDirectorDelegation, setShowDirectorDelegation] = useState(false);

  if (cards.length === 0) return null;

  const open = (card) => {
    if (card.action === 'delegate') return setShowDelegation(true);
    if (card.action === 'delegate-approval') return setShowDirectorDelegation(true);
    return navigate(card.path);
  };

  return (
    <>
      <section aria-label="Your tools" className="card-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
        {cards.map(card => (
          <HomeCard key={card.key} icon={card.icon} title={card.title} onClick={() => open(card)}>
            {card.note && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 my-1 sm:text-center">
                {card.note}
              </p>
            )}
            <p className="text-sm text-gray-600 sm:text-center">{card.blurb}</p>
          </HomeCard>
        ))}
      </section>

      {/* Inside the page body, which is deliberately not a stacking context
          (PageShell), so the modal's own z-index clears the navbar. */}
      {showDelegation && (
        <DelegationModal onClose={() => setShowDelegation(false)} />
      )}
      {showDirectorDelegation && (
        <DirectorDelegationModal onClose={() => setShowDirectorDelegation(false)} />
      )}
    </>
  );
};

export default RoleToolCards;
